// Session state for the signed-in mailbox user and (separately) an admin.
//
// The user session is mirrored to localStorage (write-through) so the app can
// boot OFFLINE: navigator can't reach /v1/me with no network, and without a
// persisted copy `user.get()` would be null on cold launch - which bounces the
// user to /login and makes the entire offline cache unreachable. The httpOnly
// session cookie remains the real credential; what we persist here is the same
// non-secret profile (email, quota) plus the csrf_token the SPA already keeps
// out of cookies (the API is on a different subdomain, so document.cookie can't
// read it). A stale csrf_token self-heals via api.js's refresh-and-retry, and
// the next online /v1/me overwrites this copy.
//
// Keyed by account slot so each Gmail-style multi-account session round-trips
// independently. The admin session stays in-memory only: admin / control-center
// screens are online-only and have no offline story.

import { accounts } from "./accounts.js";

let _user  = null;  // { email, quota_bytes, csrf_token, ... }
let _admin = null;  // { id, username, role, domain_id, csrf_token }

const SESSION_KEY = (slot) => `mc_session_user_${slot}`;

function persistUser(slot, data) {
  try {
    if (data) localStorage.setItem(SESSION_KEY(slot), JSON.stringify(data));
    else localStorage.removeItem(SESSION_KEY(slot));
  } catch { /* private mode / quota - in-memory state still works online */ }
}

function readPersistedUser(slot) {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY(slot)) ?? "null"); }
  catch { return null; }
}

export const user = {
  set: (d) => {
    _user = d;
    persistUser(accounts.activeSlot(), d);
  },
  get: () => _user,
  clear: () => {
    _user = null;
    persistUser(accounts.activeSlot(), null);
  },
  // Drop a specific slot's persisted session. Used by the multi-account
  // sign-out flows: without this a signed-out account's email + csrf token
  // would linger in localStorage and could rehydrate offline.
  clearSlot: (slot) => {
    persistUser(slot, null);
    if (slot === accounts.activeSlot()) _user = null;
  },
  csrfToken: () => _user?.csrf_token ?? null,

  // Load the persisted session for the active slot into memory. Called once at
  // boot, BEFORE the network /v1/me revalidation, so an offline (or simply
  // slow) launch renders the cached inbox instead of the login screen. No-op
  // if a session is already in memory (e.g. just signed in this tab).
  hydrate: () => {
    if (_user) return _user;
    const cached = readPersistedUser(accounts.activeSlot());
    if (cached && typeof cached === "object") _user = cached;
    return _user;
  },
};

export const admin = {
  set:          (d) => { _admin = d; },
  get:          ()  => _admin,
  clear:        ()  => { _admin = null; },
  isSuperAdmin: ()  => _admin?.role === "super_admin",
  csrfToken:    ()  => _admin?.csrf_token ?? null,
};
